/* eslint-disable react-refresh/only-export-components */
import { Icon } from '@natera/material/lib/icon'
import { cn } from '~/shared/lib/cn'
import { formatDateDefault } from '~/shared/lib/datetime'
import { Text } from '~/shared/ui'
import { NO_DATA_PLACEHOLDER } from '../../config'

export type FieldStatus = 'match' | 'contradiction' | 'indeterminate' | 'not_applicable'

export function getFieldStatus(
  fieldKey: string,
  matchedOn: string[],
  contradictedOn: string[],
): FieldStatus {
  if (contradictedOn.includes(fieldKey)) return 'contradiction'
  if (matchedOn.includes(fieldKey)) return 'match'
  return 'indeterminate'
}

export function resolveFieldStatus(
  fieldKey: string,
  matchedOn: string[],
  contradictedOn: string[],
  recordValue?: string | null,
  extractedValue?: string | null,
  options?: { isDate?: boolean },
): FieldStatus {
  if (!extractedValue) return recordValue ? 'not_applicable' : 'indeterminate'
  if (!recordValue) return 'indeterminate'
  const normalize = (v: string) => (options?.isDate ? formatDateDefault(v) : v.trim().toLowerCase())
  const frontendMatch = normalize(recordValue) === normalize(extractedValue)
  if (frontendMatch) return 'match'
  if (contradictedOn.includes(fieldKey)) return 'contradiction'
  if (matchedOn.includes(fieldKey)) return 'match'
  return 'contradiction'
}

export function FieldRow({
  label,
  value,
  status,
  hoveredField,
  hoveredFieldKey,
  extractedValue,
  isDate = false,
}: {
  label: string
  value: string | null | undefined
  status: FieldStatus
  hoveredField?: string | null
  hoveredFieldKey?: string | null
  extractedValue?: string | null
  isDate?: boolean
}) {
  const hasValue = value != null && value !== ''
  const isHighlighted = hoveredField != null && hoveredField === hoveredFieldKey
  const normalizedValue =
    hasValue && isDate ? formatDateDefault(value!) : value?.trim().toLowerCase()
  const normalizedExtracted =
    extractedValue && isDate
      ? formatDateDefault(extractedValue)
      : extractedValue?.trim().toLowerCase()
  const hoverMatches =
    isHighlighted && hasValue && !!normalizedExtracted && normalizedValue === normalizedExtracted
  return (
    <div
      className={cn('flex h-5 items-center gap-1 px-0.5 transition-colors', {
        'bg-secondary-focused': isHighlighted && hoverMatches,
        'bg-error-focused': isHighlighted && !hoverMatches,
      })}
    >
      <div className="flex min-w-0 flex-1 items-center justify-between gap-2 whitespace-nowrap">
        <Text
          className="text-text-secondary shrink-0"
          appearance="BODY2"
        >
          {label}
        </Text>
        {hasValue && (
          <Text
            className="min-w-0 text-right"
            appearance="BODY2"
            truncated
            withTooltip
            copyable
          >
            {value}
          </Text>
        )}
      </div>
      <StatusIcon status={status} />
    </div>
  )
}

export function StatusIcon({ status }: { status: FieldStatus }) {
  return (
    <Icon
      className={cn('shrink-0 text-[20px]', {
        'text-chip-status-success-content': status === 'match',
        'text-alert-error-message': status === 'contradiction',
        'text-text-secondary': status === 'indeterminate' || status === 'not_applicable',
      })}
      children={
        status === 'match'
          ? 'check'
          : status === 'contradiction'
            ? 'close'
            : status === 'not_applicable'
              ? 'block'
              : 'remove'
      }
    />
  )
}

export function VendorField({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="flex items-center gap-2.5">
      <Text
        className="text-text-secondary min-w-0 flex-1"
        appearance="BODY2"
      >
        {label}
      </Text>
      <Text
        className="min-w-0 flex-1 text-right"
        appearance="BODY2"
        placeholder={NO_DATA_PLACEHOLDER}
        truncated
        withTooltip
        copyable
      >
        {value}
      </Text>
    </div>
  )
}

export function CardDivider() {
  return <div className="border-divider my-1 border-b" />
}

export function ContradictionChip({ children }: { children: React.ReactNode }) {
  return (
    <span className="bg-error-focused text-error-main inline-flex items-center rounded px-2.5 py-1 text-sm leading-[18px] tracking-[0.15px] whitespace-nowrap">
      {children}
    </span>
  )
}

export function formatCriterion(criterion: string) {
  return criterion.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}
